import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * A19-P3 — Public unsubscribe endpoint with real backend write.
 * Path: /unsubscribe/:token
 *
 * On mount the page POSTs to `/api/unsubscribe/{token}` (idempotent). On
 * success it shows the confirmation copy. On 404 it shows an "invalid link"
 * fallback. Uses a raw axios instance (not the authenticated `api`) so a
 * logged-out invitee can also confirm.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Unsubscribe = () => {
  const { token } = useParams();
  const { t } = useLang();
  const [state, setState] = useState({ loading: true, ok: false, already: false, error: null });

  useEffect(() => {
    axios.post(`${API}/unsubscribe/${encodeURIComponent(token)}`)
      .then((r) => setState({
        loading: false,
        ok: true,
        already: !!r.data?.already,
        error: null,
      }))
      .catch((err) => setState({
        loading: false,
        ok: false,
        already: false,
        error: err?.response?.data?.detail || "errors.unknown",
      }));
  }, [token]);

  return (
    <PublicLayout>
      <div className="tsw-section py-16 max-w-xl">
        <Card data-testid="unsubscribe-card">
          <CardHeader>
            <CardTitle className="text-3xl">
              {state.loading ? (
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-7 h-7 animate-spin" />
                  …
                </span>
              ) : state.ok ? (
                <span className="inline-flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-7 h-7" />
                  {t("referrals.unsubscribe.heading")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-7 h-7" />
                  {t("referrals.errors.referral_token_not_found") || t("referrals.unsubscribe.heading")}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {state.loading && <p className="text-slate-500">…</p>}

            {state.ok && (
              <>
                <p className="text-slate-700" data-testid="unsubscribe-body">
                  {t("referrals.unsubscribe.body")}
                </p>
                <p className="text-emerald-700 font-medium" data-testid="unsubscribe-confirmed">
                  {state.already
                    ? (t("referrals.unsubscribe.alreadyConfirmed") || t("referrals.unsubscribe.confirmed"))
                    : t("referrals.unsubscribe.confirmed")}
                </p>
              </>
            )}

            {!state.ok && !state.loading && (
              <p className="text-amber-900 text-sm" data-testid="unsubscribe-error">
                {state.error === "errors.referral_token_not_found"
                  ? t("referrals.errors.referral_token_not_found")
                  : t("referrals.unsubscribe.body")}
              </p>
            )}

            {token && (
              <p className="text-xs text-slate-400 font-mono break-all">
                token: {token.slice(0, 8)}…
              </p>
            )}
            <Link to="/" className="text-emerald-700 underline" data-testid="unsubscribe-back">
              {t("referrals.unsubscribe.back")}
            </Link>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
};

export default Unsubscribe;
